import * as React from 'react';
import {Link as RouterLink} from 'react-router-dom';

import {ActionBox} from 'shells/action-box/action-box';
import {SaveButton} from 'shells/button/save-button/save-button';
import {CollapsibleCard} from 'shells/card/collapsible-card';
import {Stack} from 'shells/layout/stack/stack';
import {StackLegacy} from 'shells/layout/stack/stack-legacy';
import {Link} from 'shells/link/link';
import {LoadingText} from 'shells/loading-placeholder/loading-text';
import {CircleNumber} from 'shells/numbered-breadcrumbs/circle-number';
import {PopoverInfo} from 'shells/popover-info/popover-info';
import {StatusBadge} from 'shells/status-badge/status-badge';
import {TextField} from 'shells/textfield/textfield';
import {RelativeTimestamp} from 'shells/timestamp/relative-timestamp';
import {Body} from 'shells/typography/body';

import {DomainFeatureStatusKey} from 'nutshell-graphql-types';
import type {GetTrackingSnippetStatus_analyticsStatus as AnalyticsStatus} from 'nutshell-graphql-types';

import {EmbedAnalyticsButton} from '../../email-marketing/analytics/embed-analytics-button';

import {useGetDomainFeatureStatuses} from '../domains/graphql/hooks';

import {useTestSnippetStatus} from './graphql/hooks';
import {
    getCustomDnsDomain,
    getSnippetTestStatusMessage,
    getSnippetStatusText,
    getStatusBadgeLevel,
} from './helpers';

type Props = {
    analyticsStatus: AnalyticsStatus | null | undefined;
    isLoading: boolean | null | undefined;
    refetch: () => Promise<any>;
    refetchTrackingSnippetStatus: () => Promise<any>;
};

export function SnippetOnboardingCard(props: Props) {
    const {isLoading: isLoadingDomain, domainFeatureStatuses} = useGetDomainFeatureStatuses();

    const configuredDnsDomain =
        domainFeatureStatuses.length > 0 &&
        getCustomDnsDomain(domainFeatureStatuses, DomainFeatureStatusKey.bootSnippet);

    const hasConfiguredDnsDomain = Boolean(configuredDnsDomain);

    const hasNewSnippet = props.analyticsStatus && props.analyticsStatus.status === 'CONFIGURED';
    const hasOldSnippet =
        props.analyticsStatus && props.analyticsStatus.status === 'NEW_VERSION_AVAILABLE';

    const snippetNotWorking = Boolean(
        props.analyticsStatus && props.analyticsStatus.status === 'NOT_WORKING'
    );

    const snippetStatusText = getSnippetStatusText(
        hasOldSnippet,
        hasNewSnippet,
        hasConfiguredDnsDomain,
        snippetNotWorking
    );

    const statusBadgeLevel = getStatusBadgeLevel(
        hasOldSnippet,
        hasNewSnippet,
        hasConfiguredDnsDomain,
        snippetNotWorking
    );

    const defaultOpen =
        !hasConfiguredDnsDomain || hasOldSnippet || !hasNewSnippet || snippetNotWorking;

    const {testSnippetStatus, isLoading, error} = useTestSnippetStatus();
    const [isEnteringDomain, setIsEnteringDomain] = React.useState<boolean>(false);
    const [testDomain, setTestDomain] = React.useState<string>('');
    const [testResultMessage, setTestResultMessage] = React.useState<
        React.ReactElement<any> | null | undefined
    >(null);
    const [isOpen, setIsOpen] = React.useState<boolean>(defaultOpen);

    // @ts-expect-error
    const handleTest = (domain) => {
        setIsEnteringDomain(false);
        testSnippetStatus({domain: domain}).then((result) => {
            const snippetTestStatus =
                result.data && result.data.snippetStatusTest.snippetTestStatus;
            const status = snippetTestStatus && snippetTestStatus.status;
            const loaderDomain = snippetTestStatus && snippetTestStatus.loaderDomain;

            if (status) {
                props.refetch();
                props.refetchTrackingSnippetStatus();

                setTestResultMessage(
                    getSnippetTestStatusMessage(status, domain, loaderDomain || '')
                );
            }
        });
    };

    return (
        <CollapsibleCard
            header={
                <StackLegacy spacing={16} isHorizontal={true}>
                    <Body isBold={true} size='large' color='dark'>
                        Install the snippet
                    </Body>
                    {hasConfiguredDnsDomain && hasNewSnippet && (
                        <StatusBadge level='success' text='Snippet detected' />
                    )}
                </StackLegacy>
            }
            subheader={
                props.analyticsStatus &&
                props.analyticsStatus.domain && (
                    <div className='pt-4 flex align-center'>
                        <Body color='light'>
                            Snippet last detected on {props.analyticsStatus.domain}&nbsp;
                        </Body>
                        {props.analyticsStatus.lastBootConfigAccessTime && (
                            <RelativeTimestamp
                                datetime={props.analyticsStatus.lastBootConfigAccessTime}
                                format='fromNow'
                                className='light-text font-size-normal'
                            />
                        )}
                    </div>
                )
            }
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            fitContent={false}
            fitHeight={true}
            hasBorder={true}
            padding={24}
            isLoading={!props.analyticsStatus}
        >
            <div className='flex flex-dir-col gap-8 pt-16'>
                <ActionBox
                    icon={<CircleNumber count={1} state='selected' />}
                    title='Optional: set up your domain to prevent adblockers'
                    actionElement={
                        <>
                            <div className='flex-grow'>
                                <PopoverInfo
                                    bodyColor='dark'
                                    info='Ad blockers may prevent the snippet from loading. By configuring your domain and re-embedding the snippet, you can recover up to 11% more leads and sales.'
                                    size={24}
                                    variant='blue'
                                />
                            </div>
                            <Stack isHorizontal={true} spacing={16}>
                                {props.isLoading || isLoadingDomain ? (
                                    <LoadingText height={30} width={90} />
                                ) : (
                                    <StatusBadge
                                        level={hasConfiguredDnsDomain ? 'success' : 'warning'}
                                        text={
                                            hasConfiguredDnsDomain ? 'Configured' : 'Not configured'
                                        }
                                    />
                                )}
                                <Link
                                    variant='button-primary'
                                    size='big'
                                    to='/setup/domains'
                                    // @ts-expect-error
                                    as={RouterLink}
                                >
                                    Set up domain
                                </Link>
                            </Stack>
                        </>
                    }
                />
                <ActionBox
                    icon={<CircleNumber count={2} state='selected' />}
                    title='Add the Nutshell snippet to your site'
                    subtitle={
                        <Body color='light'>
                            Using Wordpress, Squarespace, or another CMS?{' '}
                            <Link
                                variant='primary'
                                newTab={true}
                                href='https://support.nutshell.com/en/articles/9828911-nutshell-analytics-installation-instructions'
                            >
                                Get help from our knowledge base.
                            </Link>
                        </Body>
                    }
                    actionElement={
                        <Stack isHorizontal={true} spacing={16}>
                            {props.isLoading ? (
                                <LoadingText height={30} width={90} />
                            ) : (
                                <StatusBadge level={statusBadgeLevel} text={snippetStatusText} />
                            )}
                            <EmbedAnalyticsButton buttonText='View snippet' />
                        </Stack>
                    }
                />
                <ActionBox
                    icon={<CircleNumber count={3} state='selected' />}
                    title='Run a test to confirm the snippet is installed'
                    subtitle={testResultMessage || <Body color='error'>{error}</Body>}
                    actionElement={
                        <div className='flex gap-8 align-center justify-center'>
                            {isEnteringDomain && (
                                <TextField
                                    value={testDomain}
                                    onChange={(value) => setTestDomain(value)}
                                    // @ts-expect-error
                                    autoFocus={true}
                                    placeholder='Enter domain to test'
                                    onEnter={() => {
                                        if (testDomain) {
                                            handleTest(testDomain);
                                        }
                                    }}
                                    onBlur={() => {
                                        if (!testDomain) {
                                            setIsEnteringDomain(false);
                                        }
                                    }}
                                />
                            )}
                            <SaveButton
                                variant='primary'
                                buttonText='Run test'
                                savingText='Processing...'
                                successText='Test complete'
                                // @ts-expect-error
                                onClick={() => {
                                    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                                    isEnteringDomain
                                        ? handleTest(testDomain)
                                        : setIsEnteringDomain(true);
                                }}
                                disabled={isEnteringDomain && !testDomain}
                                isSaving={isLoading}
                                resetOnComplete={true}
                                minWidthFitContent={true}
                            />
                        </div>
                    }
                />
            </div>
        </CollapsibleCard>
    );
}
